import React from 'react';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

interface PortfolioFileUploadProps {
  onUploadComplete: (portfolioData: any[]) => void;
}

interface Investment {
  id: string;
  category: string;
  name: string;
  value: string;
  purchaseDate: string;
  purpose: string;
}

// File validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = {
  'text/csv': '.csv',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx'
};
const ALLOWED_EXTENSIONS = ['.csv', '.xls', '.xlsx'];

const PortfolioFileUpload = ({ onUploadComplete }: PortfolioFileUploadProps) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 5MB';
    }

    // Check file type
    if (!ALLOWED_TYPES[file.type as keyof typeof ALLOWED_TYPES]) {
      return 'Invalid file type. Only CSV and Excel files are allowed';
    }

    // Check file extension
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
      return 'Invalid file extension. Only .csv, .xls, and .xlsx files are allowed';
    }

    // Check file name for suspicious patterns
    if (file.name.includes('../') || file.name.includes('..\\') || /[<>:"|?*]/.test(file.name)) {
      return 'Invalid file name. File name contains forbidden characters';
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setError(null);
    
    if (selectedFile) {
      const validationError = validateFile(selectedFile);
      if (validationError) {
        setError(validationError);
        e.target.value = ''; // Clear the input
        return;
      }
      setFile(selectedFile);
    } else {
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    // Re-validate file before upload
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // In a real implementation, you would process the file here
      // For demonstration, we'll simulate processing and return mock data
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock data - in a real app, you would parse the file contents
      const mockPortfolioData: Investment[] = [
        {
          id: Date.now().toString() + '1',
          category: 'stocks',
          name: 'Sample Stock Import',
          value: '10000',
          purchaseDate: '2023-01-15',
          purpose: 'Growth'
        },
        {
          id: Date.now().toString() + '2',
          category: 'mutual-funds',
          name: 'Sample Fund Import',
          value: '5000',
          purchaseDate: '2023-02-20',
          purpose: 'Retirement'
        }
      ];
      
      onUploadComplete(mockPortfolioData);
      setFile(null);
    } catch (err) {
      setError('Failed to process the file. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-background border-[#9E7C47]/20">
      <CardHeader>
        <CardTitle className="text-lg">Import Portfolio</CardTitle>
        <CardDescription>
          Upload a CSV or Excel file with your investment details
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4 bg-destructive/20 border-destructive/30">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col items-center justify-center border-2 border-dashed border-[#9E7C47]/30 rounded-md p-6 mb-4">
          <FileText className="h-8 w-8 text-[#9E7C47]/70 mb-2" />
          
          <div className="flex flex-col items-center">
            <Label 
              htmlFor="file-upload" 
              className="cursor-pointer font-medium text-[#9E7C47] hover:text-[#9E7C47]/90"
            >
              Choose file
            </Label>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
            />
            <p className="mt-1 text-sm text-white/50">
              {file ? file.name : 'CSV or Excel files only'}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <Button
            onClick={handleUpload}
            className="w-full bg-[#9E7C47] text-white hover:bg-[#9E7C47]/90"
            disabled={!file || uploading}
          >
            {uploading ? (
              <span className="flex items-center">
                <span className="animate-spin mr-2">⟳</span> Processing...
              </span>
            ) : (
              <span className="flex items-center">
                <Upload className="h-4 w-4 mr-2" /> Upload File
              </span>
            )}
          </Button>
        </div>

        <div className="mt-4 text-xs text-white/50 text-center">
          <p>Supported formats: CSV, Excel (.xlsx, .xls)</p>
          <p className="mt-1">
            Your file should include columns for investment category, name, value, purchase date, and purpose.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioFileUpload; 